import { useCallback, useEffect, useMemo, useState } from 'react'
import { AppstoreOutlined, ReloadOutlined, UnorderedListOutlined } from '@ant-design/icons'
import { Segmented } from 'antd'
import { Button } from '@crosscode/gravity-components'
import { Page } from '@/shared/Page'
import { useStore } from '@/app/StoreProvider'
import { api } from '@/api/client'
import {
  AttentionBlock,
  buildAttentionCheckers,
  buildAttentionInfra,
} from '@/features/home/AttentionBlock'
import { DeviceDetailsDrawer } from '@/features/home/DeviceDetailsDrawer'
import { DeviceGridCard } from '@/features/home/DeviceGridCard'
import { HomeServicesSummary } from '@/features/home/HomeServicesSummary'
import { InfraTable } from '@/features/home/InfraTable'
import { PriceCheckerDetailsDrawer } from '@/features/home/PriceCheckerDetailsDrawer'
import { PriceCheckerGridCard } from '@/features/home/PriceCheckerGridCard'
import { PriceCheckerTable } from '@/features/home/PriceCheckerTable'
import {
  buildInfraRow,
  formatPing,
  labelToType,
  readViewMode,
  type InfraTableRow,
  type InfraNode,
  type PriceChecker,
  type ViewMode,
} from '@/features/home/utils'
import styles from './HomePage.module.css'

export function HomePage() {
  const { storeId, selectedStore } = useStore()
  const [view, setView] = useState<ViewMode>(readViewMode)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [nodes, setNodes] = useState<InfraNode[]>([])
  const [utmByNode, setUtmByNode] = useState<Record<string, Record<string, unknown>>>({})
  const [chzByNode, setChzByNode] = useState<Record<string, Record<string, unknown>>>({})
  const [priceCheckers, setPriceCheckers] = useState<PriceChecker[]>([])
  const [checkerStatus, setCheckerStatus] = useState<Record<string, string>>({})
  const [selectedInfra, setSelectedInfra] = useState<InfraTableRow | null>(null)
  const [selectedChecker, setSelectedChecker] = useState<PriceChecker | null>(null)
  const [servicesRefreshKey, setServicesRefreshKey] = useState(0)

  const setViewMode = (next: ViewMode) => {
    setView(next)
    try {
      localStorage.setItem('kit.home.view', next)
    } catch {
      /* ignore */
    }
  }

  const load = useCallback(async () => {
    if (!storeId) {
      setNodes([])
      setPriceCheckers([])
      return
    }
    setLoading(true)
    setError(null)
    try {
      const storeData = await api<{ name?: string; nodes?: InfraNode[]; ipDetails?: InfraNode[] }>(
        `/api/stores/${storeId}`,
      )
      const infra = storeData.nodes || storeData.ipDetails || []
      setNodes(infra)

      const utm: Record<string, Record<string, unknown>> = {}
      const chz: Record<string, Record<string, unknown>> = {}
      await Promise.all(
        infra.map(async (n) => {
          const t = labelToType(n.label || n.name, n.type)
          const key = String(n.id ?? 'legacy')
          if (t === 'egais_utm') {
            try {
              const url = n.id ? `/api/nodes/${n.id}/egais-utm` : `/api/stores/${storeId}/egais-utm`
              utm[key] = await api(url)
            } catch (e) {
              utm[key] = { error: e instanceof Error ? e.message : 'Ошибка ЕГАИС УТМ' }
            }
          }
          if (t === 'chz') {
            try {
              const url = n.id ? `/api/nodes/${n.id}/chz/status` : `/api/stores/${storeId}/chz/status`
              chz[key] = await api(url)
            } catch (e) {
              chz[key] = { error: e instanceof Error ? e.message : 'Ошибка Честного Знака' }
            }
          }
        }),
      )
      setUtmByNode(utm)
      setChzByNode(chz)

      try {
        const pcs = await api<PriceChecker[]>(`/api/stores/${storeId}/price-checkers`)
        setPriceCheckers(Array.isArray(pcs) ? pcs : [])
      } catch {
        setPriceCheckers([])
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ошибка загрузки магазина')
      setNodes([])
    } finally {
      setLoading(false)
    }
  }, [storeId])

  useEffect(() => {
    void load()
  }, [load])

  useEffect(() => {
    setNodes([])
    setPriceCheckers([])
    setUtmByNode({})
    setChzByNode({})
    setCheckerStatus({})
    setSelectedInfra(null)
    setSelectedChecker(null)
  }, [storeId])

  useEffect(() => {
    if (!priceCheckers.length) return
    let cancelled = false
    ;(async () => {
      const next: Record<string, string> = {}
      await Promise.all(
        priceCheckers.map(async (c) => {
          try {
            const s = await api<{ status?: string; online?: boolean; ms?: string }>(
              `/api/price-checkers/${c.id}/status`,
            )
            const online = s.online ?? s.status === 'online'
            next[String(c.id)] = online
              ? `Статус: Онлайн${s.ms ? ` ${formatPing(s.ms) || ''}` : ''}`
              : 'Статус: Оффлайн'
          } catch {
            next[String(c.id)] = 'Статус неизвестен'
          }
        }),
      )
      if (!cancelled) setCheckerStatus(next)
    })()
    return () => {
      cancelled = true
    }
  }, [priceCheckers])

  const infraRows = useMemo(() => nodes.map((n, i) => buildInfraRow(n, i)), [nodes])

  const onlineCount = nodes.filter((n) => n.status === 'online').length
  const checkerOnline = priceCheckers.filter((c) => {
    const s = checkerStatus[String(c.id)]
    return s && /онлайн/i.test(s)
  }).length

  const attentionInfra = buildAttentionInfra(infraRows, utmByNode, chzByNode)
  const attentionCheckers = buildAttentionCheckers(priceCheckers, checkerStatus)
  const problemCount = attentionInfra.length + attentionCheckers.length

  const reloadAll = useCallback(() => {
    void load()
    setServicesRefreshKey((k) => k + 1)
  }, [load])

  const openInfraById = (id: string) => {
    const row = infraRows.find((r) => r.nodeId === id)
    if (row) setSelectedInfra(row)
  }

  const openCheckerById = (id: string) => {
    const checker = priceCheckers.find((c) => String(c.id) === id)
    if (checker) setSelectedChecker(checker)
  }

  const infraOnline = infraRows.filter((r) => r.status === 'online').length
  const infraOffline = infraRows.length - infraOnline
  const checkerOffline = priceCheckers.length - checkerOnline

  const infraContent =
    view === 'list' ? (
      <div className={styles.panel}>
        <div className={styles.panelHead}>
          <h2 className={styles.panelTitle}>Инфраструктура</h2>
          <div className={styles.badges}>
            <span className={styles.badgeTotal}>{infraRows.length}</span>
            <span className={styles.badgeOn}>{infraOnline}</span>
            {infraOffline > 0 ? <span className={styles.badgeOff}>{infraOffline}</span> : null}
          </div>
        </div>
        <InfraTable
          nodes={nodes}
          storeId={storeId!}
          utmByNode={utmByNode}
          chzByNode={chzByNode}
          problemsOnly={false}
          onReload={load}
          onSelectRow={setSelectedInfra}
        />
      </div>
    ) : (
      <div className={styles.grid}>
        {infraRows.map((row) => {
          const hasDetails = row.nodeType === 'chz' || row.nodeType === 'egais_utm'
          return (
            <div
              key={row.nodeId}
              role="presentation"
              onClick={hasDetails ? undefined : () => setSelectedInfra(row)}
              className={hasDetails ? undefined : styles.gridCardClickable}
            >
              <DeviceGridCard
                node={row}
                id={row.nodeId}
                storeId={storeId!}
                utm={utmByNode[row.nodeId]}
                chz={chzByNode[row.nodeId]}
                onReload={load}
                onShowDetails={hasDetails ? () => setSelectedInfra(row) : undefined}
              />
            </div>
          )
        })}
      </div>
    )

  const checkerContent =
    view === 'list' ? (
      <div className={styles.panel}>
        <div className={styles.panelHead}>
          <h2 className={styles.panelTitle}>Прайс-чекеры</h2>
          <div className={styles.badges}>
            <span className={styles.badgeTotal}>{priceCheckers.length}</span>
            <span className={styles.badgeOn}>{checkerOnline}</span>
            {checkerOffline > 0 ? <span className={styles.badgeOff}>{checkerOffline}</span> : null}
          </div>
        </div>
        <PriceCheckerTable
          checkers={priceCheckers}
          checkerStatus={checkerStatus}
          problemsOnly={false}
          onSelectRow={setSelectedChecker}
        />
      </div>
    ) : (
      <div className={styles.grid}>
        {priceCheckers.map((checker) => (
          <div
            key={String(checker.id)}
            role="presentation"
            onClick={() => setSelectedChecker(checker)}
            className={styles.gridCardClickable}
          >
            <PriceCheckerGridCard
              checker={checker}
              statusLabel={checkerStatus[String(checker.id)]}
            />
          </div>
        ))}
      </div>
    )

  const hasDevices = nodes.length > 0 || priceCheckers.length > 0

  return (
    <Page
      title="Главная"
      count={
        storeId && hasDevices
          ? `${onlineCount + checkerOnline}/${nodes.length + priceCheckers.length}`
          : undefined
      }
      subtitle={selectedStore?.name || 'Выберите магазин'}
      actions={
        <div className={styles.actionsBar}>
          <Segmented
            size="small"
            value={view}
            onChange={(v) => setViewMode(v as ViewMode)}
            options={[
              { label: 'Список', value: 'list', icon: <UnorderedListOutlined /> },
              { label: 'Плитки', value: 'grid', icon: <AppstoreOutlined /> },
            ]}
          />
          <Button icon={<ReloadOutlined />} onClick={reloadAll} disabled={!storeId} loading={loading}>
            Обновить
          </Button>
        </div>
      }
      error={error}
      empty={!storeId}
      emptyText="Выберите магазин"
      emptyDescription="Магазин выбирается внизу боковой панели"
      onRetry={reloadAll}
    >
      {storeId ? (
        <HomeServicesSummary
          storeId={storeId}
          refreshKey={servicesRefreshKey}
          utmByNode={utmByNode}
          chzByNode={chzByNode}
          devicesLoading={loading}
        />
      ) : null}

      {storeId && problemCount > 0 ? (
        <AttentionBlock
          infra={attentionInfra}
          checkers={attentionCheckers}
          onSelectInfra={openInfraById}
          onSelectChecker={openCheckerById}
        />
      ) : null}

      {storeId && nodes.length > 0 ? (
        <div className={styles.section}>{infraContent}</div>
      ) : null}

      {storeId && priceCheckers.length > 0 ? (
        <div className={styles.section}>{checkerContent}</div>
      ) : null}

      {storeId && !hasDevices && loading ? (
        <div className={styles.devicesLoading}>Загрузка устройств…</div>
      ) : null}

      <DeviceDetailsDrawer
        open={!!selectedInfra}
        row={selectedInfra}
        storeId={storeId || ''}
        utm={selectedInfra ? utmByNode[selectedInfra.nodeId] : undefined}
        chz={selectedInfra ? chzByNode[selectedInfra.nodeId] : undefined}
        onClose={() => setSelectedInfra(null)}
        onReload={load}
      />

      <PriceCheckerDetailsDrawer
        open={!!selectedChecker}
        checker={selectedChecker}
        statusLabel={selectedChecker ? checkerStatus[String(selectedChecker.id)] : undefined}
        onClose={() => setSelectedChecker(null)}
      />
    </Page>
  )
}
